import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getRetainerStatus } from '../redux/ducks/billingRetainers';
import { setRetainerStatus } from '../redux/slices/retainerStatus';
import { useHasPermission } from '../utils/permissionUtils';

export const useRetainerStatus = ({ accountId, clientId, enabled = true } = {}) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission('canSeeInternal');
    const [loading, setLoading] = useState(false);
    const isMountedRef = useRef(true);
    const inFlightKeyRef = useRef(null);

    const key = accountId ? `account:${accountId}` : clientId ? `client:${clientId}` : null;
    const cached = useSelector((state) => (key ? state.retainerStatus.byKey[key] : undefined));

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!enabled || !canSeeInternal || !key) {
            setLoading(false);
            return;
        }
        if (cached !== undefined) {
            setLoading(false);
            return;
        }
        if (inFlightKeyRef.current === key) {
            return;
        }

        inFlightKeyRef.current = key;
        setLoading(true);
        dispatch(
            getRetainerStatus({ accountId, clientId }, (data) => {
                inFlightKeyRef.current = null;
                if (!isMountedRef.current) return;
                if (data) {
                    dispatch(setRetainerStatus({ key, status: data }));
                }
                setLoading(false);
            })
        );
    }, [dispatch, accountId, clientId, enabled, canSeeInternal, key, cached]);

    return { loading, status: cached === undefined ? null : cached };
};

export default useRetainerStatus;
